import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Calendar, Clock, Tag } from 'lucide-react';
import { getDisplayName } from '../../../../lib/utils';
import ProgramsSection from './ProgramsSection';
import OneTimeJobsSection from './OneTimeJobsSection';
import FlashPill from '../../../../components/propertyDetails/FlashPill';
import CreditBalanceCard from '../../../../components/credits/CreditBalanceCard';

/**
 * OverviewTab - Client overview information
 */
const OverviewTab = ({ client }) => {
  if (!client) return null;

  // Format address object into readable string
  const formatAddress = (address) => {
    if (!address) return 'No address';
    
    // Handle string addresses
    if (typeof address === 'string') {
      return address;
    }
    
    // Handle object addresses
    if (typeof address === 'object') {
      const { streetNumber, streetName, street, city, state, zip } = address;
      const streetPart = street || `${streetNumber || ''} ${streetName || ''}`.trim();
      const parts = [streetPart, city, state, zip].filter(Boolean);
      return parts.length > 0 ? parts.join(', ') : 'No address';
    }
    
    return 'No address';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      let date;
      if (timestamp.toDate) {
        date = timestamp.toDate();
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      } else {
        return 'N/A';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const formatRelativeDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      let date;
      if (timestamp.toDate) {
        date = timestamp.toDate();
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      } else {
        return 'N/A';
      }
      
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      return 'N/A';
    }
  };

  return (
    <div className="space-y-6">
      {/* Flash note — bright property-level pill at the top of the overview */}
      {client?.propertyDetails?.flashNote && (
        <FlashPill propertyDetails={client.propertyDetails} testId="overview-flash-pill" className="max-w-full" />
      )}

      {/* Client Details */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Client Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-500">Name</label>
              <p className="text-gray-900">{getDisplayName(client, '-')}</p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-500">Email</label>
              <p className="text-gray-900">{client.email || '-'}</p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-500">Phone</label>
              <p className="text-gray-900">{client.phone || '-'}</p>
            </div>
          </div>
          
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-500">Address</label>
              <p className="text-gray-900">{formatAddress(client.address)}</p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-500">Pipeline Stage</label>
              <p className="text-gray-900">{client.stage}</p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-500">Lead Source</label>
              <p className="text-gray-900">{client.source || '-'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recurring Programs — Sprint 1 unified section (folds in Service Plan,
          Upcoming Visits, and Prepay Package status) */}
      <ProgramsSection client={client} />

      {/* One-Time Jobs — Phase B at-a-glance surface for ad-hoc visits.
          Operational focus only (upcoming + active). Full historical record
          lives in the Jobs tab. */}
      <OneTimeJobsSection client={client} />

      {/* Account Credit (iter_223, Phase 1) */}
      <CreditBalanceCard companyId={client.companyId} leadId={client.id} />

      {/* Timeline Information */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Timeline</h3>
        <div className="space-y-3">
          <div className="flex items-center gap-3 text-gray-600">
            <Calendar className="h-5 w-5 text-gray-400" />
            <div>
              <span className="text-sm font-medium">Created:</span>
              <span className="text-sm ml-2">
                {formatDate(client.createdAt)} ({formatRelativeDate(client.createdAt)})
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-3 text-gray-600">
            <Clock className="h-5 w-5 text-gray-400" />
            <div>
              <span className="text-sm font-medium">Last Updated:</span>
              <span className="text-sm ml-2">
                {formatDate(client.updatedAt)} ({formatRelativeDate(client.updatedAt)})
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Notes Section */}
      {client.notes && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Notes</h3>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <p className="text-gray-700 whitespace-pre-wrap">{client.notes}</p>
          </div>
        </div>
      )}

      {/* Stage History Placeholder */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Stage History</h3>
        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 text-center">
          <Tag className="h-8 w-8 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">Stage history tracking coming soon</p>
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;
